import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import heroImg from "../components/assets/images/hero.png";
import heroImgback from "../components/assets/images/hero-shape-purple.png";
import { FaHandsHelping, FaHandHoldingHeart, FaHeart, FaUsers } from "react-icons/fa";
import About1 from "./About1";
import About2 from "./About2";
import StickyWhatsApp from "./StickyWhatsApp";

/* ─── Design tokens ──────────────────────────────────────────────── */
const tokens = {
  cream: "#F7F4EF",
  teal: "#5CE1E6",
  tealDark: "#2AB8BF",
  slate: "#1C2B35",
  muted: "#6B7E87",
  pill: "rgba(255,255,255,0.85)",
};

/* ─── Badge / pill card ──────────────────────────────────────────── */
const Pill = ({ icon: Icon, label, sublabel, delay, className = "" }) => (
  <motion.div
    className={`pill-card ${className}`}
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
  >
    <span className="pill-icon">
      <Icon size={18} />
    </span>
    <span className="pill-text">
      {label}
      {sublabel && <em>{sublabel}</em>}
    </span>
  </motion.div>
);

/* ─── HomeContent ────────────────────────────────────────────────── */
export const HomeContent = () => {
  const navigate = useNavigate();

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=DM+Sans:wght@300;400;500&display=swap');

        :root {
          --cream: ${tokens.cream};
          --teal: ${tokens.teal};
          --teal-dark: ${tokens.tealDark};
          --slate: ${tokens.slate};
          --muted: ${tokens.muted};
        }

        /* ── Hero section ── */
        .hero-section {
          min-height: 100vh;
          background: var(--cream);
          display: flex;
          align-items: stretch;
          position: relative;
          overflow: hidden;
          font-family: 'DM Sans', sans-serif;
        }

        /* Subtle noise texture overlay */
        .hero-section::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E");
          pointer-events: none;
          z-index: 0;
        }

        /* Teal accent bar — left edge */
        .hero-section::after {
          content: '';
          position: absolute;
          left: 0;
          top: 15%;
          height: 55%;
          width: 5px;
          background: linear-gradient(180deg, transparent, var(--teal), transparent);
          border-radius: 0 4px 4px 0;
          z-index: 1;
        }

        .hero-inner {
          display: grid;
          grid-template-columns: 1fr 1fr;
          width: 100%;
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 3rem;
          position: relative;
          z-index: 2;
        }

        /* ── Left column ── */
        .hero-left {
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 6rem 4rem 6rem 2rem;
          gap: 0;
        }

        .hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 0.7rem;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--teal-dark);
          margin-bottom: 1.5rem;
        }

        .hero-eyebrow span {
          display: inline-block;
          width: 28px;
          height: 1.5px;
          background: var(--teal-dark);
        }

        .hero-heading {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: clamp(2.6rem, 4.5vw, 4rem);
          font-weight: 700;
          line-height: 1.08;
          color: var(--slate);
          margin: 0 0 1.75rem;
          letter-spacing: -0.02em;
        }

        .hero-heading em {
          font-style: italic;
          color: var(--teal-dark);
        }

        .hero-sub {
          font-size: 1rem;
          font-weight: 300;
          color: var(--muted);
          line-height: 1.65;
          max-width: 380px;
          margin-bottom: 2.5rem;
        }

        .hero-cta {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: var(--slate);
          color: #fff;
          border: none;
          padding: 0.9rem 2rem;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.85rem;
          font-weight: 500;
          letter-spacing: 0.04em;
          border-radius: 2px;
          cursor: pointer;
          width: fit-content;
          transition: background 0.25s, transform 0.2s;
          position: relative;
          overflow: hidden;
        }

        .hero-cta::after {
          content: '';
          position: absolute;
          left: 0; bottom: 0;
          width: 100%; height: 3px;
          background: var(--teal);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.3s ease;
        }

        .hero-cta:hover { background: var(--teal-dark); transform: translateY(-1px); }
        .hero-cta:hover::after { transform: scaleX(1); }

        .cta-arrow {
          display: inline-block;
          transition: transform 0.25s;
        }
        .hero-cta:hover .cta-arrow { transform: translateX(4px); }

        /* ── Stat strip ── */
        .hero-stats {
          display: flex;
          gap: 2rem;
          margin-top: 3.5rem;
          padding-top: 2.5rem;
          border-top: 1px solid rgba(28,43,53,0.1);
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .stat-num {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.9rem;
          font-weight: 700;
          color: var(--slate);
          line-height: 1;
        }

        .stat-label {
          font-size: 0.7rem;
          font-weight: 400;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--muted);
        }

        /* ── Right column ── */
        .hero-right {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Teal blob behind image */
        .hero-blob {
          position: absolute;
          width: 78%;
          height: 78%;
          background: radial-gradient(ellipse at 60% 50%, rgba(92,225,230,0.28) 0%, transparent 70%);
          border-radius: 50%;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }

        .hero-img-wrap {
          position: relative;
          z-index: 2;
          width: 90%;
          max-width: 480px;
        }

        .hero-img-back {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: contain;
          z-index: 1;
          opacity: 0.7;
        }

        .hero-img-main {
          position: relative;
          z-index: 2;
          width: 100%;
          height: 100%;
          object-fit: contain;
          display: block;
          filter: drop-shadow(0 24px 48px rgba(28,43,53,0.12));
        }

        /* ── Pill cards ── */
        .pill-card {
          position: absolute;
          display: flex;
          align-items: center;
          gap: 10px;
          background: ${tokens.pill};
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.9);
          border-radius: 50px;
          padding: 0.55rem 1.1rem 0.55rem 0.55rem;
          box-shadow: 0 8px 32px rgba(28,43,53,0.1);
          z-index: 10;
          cursor: default;
          white-space: nowrap;
        }

        .pill-icon {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: var(--teal);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .pill-text {
          font-family: 'DM Sans', sans-serif;
          font-size: 0.78rem;
          font-weight: 500;
          color: var(--slate);
          display: flex;
          flex-direction: column;
          gap: 1px;
        }

        .pill-text em {
          font-style: normal;
          font-weight: 300;
          font-size: 0.7rem;
          color: var(--muted);
        }

        /* Pill positions */
        .pill-quality  { top: 18%;  left: -8%; }
        .pill-heart    { top: 8%;   right: 6%; }
        .pill-team     { bottom: 22%; right: -6%; }
        .pill-response { bottom: 12%; left: 12%; }

        /* ── Responsive ── */
        @media (max-width: 900px) {
          .hero-inner {
            grid-template-columns: 1fr;
            padding: 0 1.5rem;
          }
          .hero-left {
            padding: 5rem 1rem 2rem;
            align-items: center;
            text-align: center;
          }
          .hero-heading, .hero-sub { text-align: center; }
          .hero-sub { max-width: 100%; }
          .hero-eyebrow { justify-content: center; }
          .hero-cta { align-self: center; }
          .hero-stats { justify-content: center; }
          .hero-right { min-height: 420px; padding: 2rem 0 4rem; }
          .hero-img-wrap { width: 75%; }
          .pill-quality  { top: 5%;   left: 0; }
          .pill-heart    { top: 2%;   right: 2%; }
          .pill-team     { bottom: 8%; right: 0; }
          .pill-response { bottom: 2%; left: 10%; }
        }

        @media (max-width: 520px) {
          .hero-heading { font-size: 2.1rem; }
          .hero-stats { flex-wrap: wrap; gap: 1.5rem; }
        }
      `}</style>

      <section className="hero-section">
        <div className="hero-inner">

          {/* ── LEFT ── */}
          <div className="hero-left">
            <motion.p
              className="hero-eyebrow"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span /> Trusted Healthcare
            </motion.p>

            <motion.h1
              className="hero-heading"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              Compassionate Care <br />
              for a <em>Healthier</em> Tomorrow
            </motion.h1>

            <motion.p
              className="hero-sub"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              You are guaranteed to find the right care for you
            </motion.p>

            <motion.button
              className="hero-cta"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              onClick={() => navigate("/contact")}
            >
              Get Started <span className="cta-arrow">→</span>
            </motion.button>

            <motion.div
              className="hero-stats"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.55 }}
            >
              <div className="stat-item">
                <span className="stat-num">500+</span>
                <span className="stat-label">Clients served</span>
              </div>
              <div className="stat-item">
                <span className="stat-num">98%</span>
                <span className="stat-label">Satisfaction rate</span>
              </div>
              <div className="stat-item">
                <span className="stat-num">24/7</span>
                <span className="stat-label">Available care</span>
              </div>
            </motion.div>
          </div>

          {/* ── RIGHT ── */}
          <div className="hero-right">
            <div className="hero-blob" />

            <motion.div
              className="hero-img-wrap"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            >
              <img src={heroImgback} alt="" className="hero-img-back" />
              <img src={heroImg} alt="Caregiver" className="hero-img-main" />
            </motion.div>

            {/* Pills */}
            <Pill
              icon={FaHandsHelping}
              label="High Quality Care"
              delay={0.4}
              className="pill-quality"
            />
            <Pill
              icon={FaHandHoldingHeart}
              label="Compassionate Support"
              delay={0.5}
              className="pill-heart"
            />
            <Pill
              icon={FaUsers}
              label="Professional Team"
              sublabel="of Care-Givers"
              delay={0.6}
              className="pill-team"
            />
            <Pill
              icon={FaHeart}
              label="Responsive Care"
              delay={0.7}
              className="pill-response"
            />
          </div>
        </div>
      </section>
    </>
  );
};

/* ─── Home page ──────────────────────────────────────────────────── */
export const Home = () => (
  <>
    <HomeContent />
    <About1 />
    <br />
    <StickyWhatsApp />
    <br />
    <About2 />
    <br />
  </>
);